<template>
  <div>
    <div class="container container-sm">
      <div class="row justify-content-center section" style="margin: 0 auto">
        <div v-if="success">
          <Card class="mb-4">
            <template #title><h3 class="text24-blue">{{ t("COURSE_REG_UNSUB_SUCCESS_TITLE") }}</h3></template>
            <template #content>
              <p>{{ t("COURSE_REG_UNSUB_SUCCESS_TEXT") }}</p>
              <p v-if="alreadyUnsubscribed">{{ t("COURSE_REG_UNSUB_ALREADY_TEXT") }}</p>
            </template>
          </Card>
        </div>

        <div v-else-if="tokenValid">
          <Card class="mb-4">
            <template #title><h3 class="text24-blue">{{ t("COURSE_REG_UNSUB_TITLE") }}</h3></template>
            <template #content>
              <p class="fw-bold">{{ t("COURSE_REG_UNSUB_QUESTION") }}</p>

              <form novalidate @submit.prevent="submitUnsubscribe">
                <div
                    v-for="reason in reasonOptions"
                    :key="reason"
                    class="mb-2 d-flex align-items-start gap-2"
                >
                  <Checkbox
                      v-model="selectedReasons"
                      :input-id="`reason-${reason}`"
                      name="reasons"
                      :value="reason"
                  />
                  <label :for="`reason-${reason}`" class="mb-0">{{ t(`COURSE_REG_UNSUB_REASON.${reason}`) }}</label>
                </div>

                <div v-if="otherReasonSelected" class="mb-2">
                  <Textarea
                      v-model="otherReasonText"
                      class="w-100"
                      rows="3"
                      maxlength="2000"
                      :placeholder="t('COURSE_REG_UNSUB_OTHER_PLACEHOLDER')"
                      :invalid="showValidation && otherReasonTextInvalid"
                  />
                  <small v-if="showValidation && otherReasonTextInvalid" class="text-danger d-block mt-1">
                    {{ t("COURSE_REG_UNSUB_OTHER_REQUIRED") }}
                  </small>
                </div>

                <small v-if="showValidation && noReasonSelected" class="text-danger d-block mb-2">
                  {{ t("COURSE_REG_UNSUB_REASONS_REQUIRED") }}
                </small>

                <Button
                    type="submit"
                    :label="t('COURSE_REG_UNSUB_SUBMIT')"
                    class="blue mt-3"
                />
              </form>
            </template>
          </Card>
        </div>

        <div v-show="invalidToken" class="alert alert-danger">{{ t("COURSE_REG_UNSUB_INVALID_TOKEN") }}</div>
      </div>

      <div class="separator-line"></div>

      <div class="mt-4">
        <router-link :to="{ name: 'Home' }" class="p-button mb-4">{{ t("BACK") }}</router-link>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref} from "vue";
import {injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {useToast} from "@/util/toastUtils";

const OTHER_REASON = "OTHER";

const REASON_OPTIONS = [
  "INFO_FROM_PROFILE",
  "NOT_RELEVANT",
  "COMPLETED_ALL_COURSES",
  "TOO_FREQUENT",
  "PREFER_OTHER_CHANNEL",
  "NO_LONGER_USING",
  OTHER_REASON,
];

const router = useRouter();
const route = useRoute();
const {t} = useI18n();
const toast = useToast();
const axios = injectAxios();

const tokenValid = ref(false);
const invalidToken = ref(false);
const success = ref(false);
const alreadyUnsubscribed = ref(false);
const selectedReasons = ref([]);
const otherReasonText = ref("");
const showValidation = ref(false);

const reasonOptions = REASON_OPTIONS;

const otherReasonSelected = computed(() => selectedReasons.value.includes(OTHER_REASON));

const otherReasonTextInvalid = computed(() => otherReasonSelected.value && !otherReasonText.value.trim());

const noReasonSelected = computed(() => {
  const predefinedSelected = selectedReasons.value.some((reason) => reason !== OTHER_REASON);
  return !predefinedSelected && !otherReasonSelected.value;
});

const reasonsInvalid = computed(() => noReasonSelected.value || otherReasonTextInvalid.value);

onMounted(async () => {
  if (route.query.token) {
    await checkToken(route.query.token);
  } else {
    await router.push({path: "/"});
  }
});

async function checkToken(token) {
  try {
    const response = await axios.post("/account/course-registration-unsubscribe/token-valid", {token});
    tokenValid.value = response.data.valid;
    invalidToken.value = !response.data.valid;
    alreadyUnsubscribed.value = response.data.alreadyUnsubscribed;
    if (response.data.alreadyUnsubscribed) {
      success.value = true;
      tokenValid.value = false;
    }
  } catch {
    invalidToken.value = true;
    tokenValid.value = false;
  }
}

async function submitUnsubscribe() {
  showValidation.value = true;
  if (reasonsInvalid.value) {
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
    return;
  }

  const reasons = selectedReasons.value.filter((reason) => reason !== OTHER_REASON);
  const trimmedOtherReason = otherReasonText.value.trim();
  const payload = {
    token: route.query.token,
    reasons,
  };
  if (trimmedOtherReason) {
    payload.otherReasonText = trimmedOtherReason;
  }

  try {
    await axios.post("/account/course-registration-unsubscribe", payload);
    success.value = true;
    tokenValid.value = false;
    toast.makeSuccessToast(t("OPERATION_SUCCESSFUL"));
  } catch {
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
  }
}
</script>
